import { useMemo } from 'react';
import type {
  SignalFieldMappings,
  ModelSchema,
  OutputDefinition,
} from '@arcanum-hq/shared-types';
import { useModel } from './use-models-v2';
import { useConversation } from './use-conversations-v2';
import { useModelView } from './use-model-view';
import { usePublicDeal, useSharedModelView } from './use-public-deal';

// deal-signal-overhaul Phase 4 Session 1 (2026-05-11).
//
// `useSignalFieldResolver` resolves SignalFieldMappings keys → engine-computed
// values. Single hook + symmetric resolution across BOTH auth (Builder Signal)
// and public (Deal Link Signal) surfaces.
//
// Auth path  → useModelView(conversationId).data → cellValues + inputValues
// Public path → useSharedModelView(shareId).data → cellValues + inputValues
//
// Both data sources are `ModelViewPayload` (same discriminated-union shape),
// so the resolver logic is identical once the source is selected.
//
// Resolution algorithm:
//   1. Look up `mapping = signalFieldMappings[key]` (the named-range identifier).
//      Returns `null` when the mapping is absent — variant-correct miss
//      (e.g. asking `usesLandCost` on op-asset which has no uses keys).
//   2. Try `inputValues[mapping]` (covers input-typed fields like
//      snapshotAddress → 'PropertyAddress'). Returns the value if present
//      and non-null.
//   3. Fall back to walking `schema.outputs[]` for `out.name === mapping`,
//      then reading `cellValues['${out.sheet}!${out.cell}']`. Returns the
//      cell value if found.
//   4. Returns `undefined` when mapping is present but the resolved value is
//      missing (data gap — distinct from `null` for variant-correct miss).
//
// Pre-2026-05-11 the candidate-key fallback in <SignalOperatingAsset> +
// <SignalNewDevelopment> walked HARDCODED arrays of name candidates. This
// hook replaces that pattern with mappings-driven resolution.

export type SignalFieldResolverContext =
  | { kind: 'conversation'; conversationId: string }
  | { kind: 'public-deal'; shareId: string };

export interface SignalFieldResolver {
  /**
   * Resolve a typed SignalFieldMappings key. Returns:
   *   - `null` when the model's mapping is absent for this key (variant-correct miss)
   *   - `undefined` when the mapping is present but the value can't be resolved (data gap)
   *   - The resolved value otherwise (number | string | boolean | unknown)
   */
  resolve(key: keyof SignalFieldMappings): unknown;
  /**
   * Resolve a raw string key — supports template tokens that may reference
   * mapping keys (preferred) OR direct named-range identifiers (escape hatch).
   * Same logic as `resolve` for mapping-keyed inputs; falls back to direct
   * `inputValues[key]` / `cellValues` lookup when no mapping matches.
   */
  resolveTemplate(key: string): unknown;
  /** True once both the model + the data source have loaded. */
  isReady: boolean;
  /**
   * The resolved ModelSchema. Auth path: from `useModel(modelId)`. Public
   * path: from `sharedModelView.data.schema` (extended 2026-05-13 to carry
   * `displayConfig` + `signal_variant`). `undefined` while loading OR when
   * the resolver can't resolve a model (e.g. invalid shareId). Consumers
   * SHOULD read from `resolver.schema` rather than calling `useModel`
   * directly — otherwise public viewers 401 on `/api/models/:id` and stay
   * in `data-state="loading"` forever (caught by `/e2e-walk` 2026-05-13).
   */
  schema: ModelSchema | undefined;
}

interface ResolutionInputs {
  mappings: SignalFieldMappings | undefined;
  schema: ModelSchema | undefined;
  inputValues: Record<string, unknown>;
  cellValues: Record<string, unknown>;
}

function resolveByMapping(
  inputs: ResolutionInputs,
  mappingKey: keyof SignalFieldMappings,
): unknown {
  const namedRange = inputs.mappings?.[mappingKey];
  if (namedRange == null) return null; // mapping absent — variant-correct miss
  return resolveByNamedRange(inputs, namedRange);
}

function resolveByNamedRange(
  inputs: ResolutionInputs,
  namedRange: string,
): unknown {
  // Inputs first — catches input-typed fields (PropertyAddress, etc.).
  const fromInputs = inputs.inputValues[namedRange];
  if (fromInputs != null) return fromInputs;

  // Walk schema.outputs for the matching output → read cellValues.
  const output = inputs.schema?.outputs.find(
    (o: OutputDefinition) => o.name === namedRange,
  );
  if (output) {
    const cellKey = `${output.sheet}!${output.cell}`;
    const fromCells = inputs.cellValues[cellKey];
    if (fromCells != null) return fromCells;
  }

  // `deal-signal-overhaul` Phase 5 hotfix (2026-05-12) — table-row default
  // fallback for `<UnitMixBar>` (and any future table-row-cell consumer).
  //
  // Table-input cells are INPUT-typed: their named ranges (e.g.
  // `Studio_Units`, `T_1_Bed_Units`) are NOT in `schema.outputs[]` and NOT
  // surfaced in `cellValues` (which projects formula outputs only). They
  // also don't land in `agent_state.inputValues` until the user confirms a
  // `propose_table_fill` proposal — meaning every Signal-page render between
  // model creation and the first user-confirmed table edit returns
  // `undefined` for these mappings, and `<UnitMixBar>` collapses to its
  // "No unit-mix data" empty state.
  //
  // The pragmatic fix: walk `schema.tableInputs[].rows[].cells[]` looking
  // for the named range, then return the matching row's
  // `defaultValues[colKey]`. This surfaces the workbook's authored defaults
  // until the user explicitly overrides them via the TABLE PROPOSAL UX.
  // Once they confirm, `inputValues[namedRange]` becomes non-null and the
  // first branch wins — same code path the agent-set scalar inputs use.
  const tableInputs = (inputs.schema as { tableInputs?: unknown[] } | undefined)
    ?.tableInputs;
  if (Array.isArray(tableInputs)) {
    for (const table of tableInputs) {
      const t = table as {
        rows?: Array<{
          cells?: Record<string, string>;
          defaultValues?: Record<string, unknown>;
        }>;
      };
      const rows = t.rows ?? [];
      for (const row of rows) {
        const cells = row.cells ?? {};
        for (const colKey of Object.keys(cells)) {
          if (cells[colKey] === namedRange) {
            const def = row.defaultValues?.[colKey];
            if (def != null) return def;
          }
        }
      }
    }
  }

  // Final fallback for direct-templates: maybe the named-range matches an
  // input that hasn't been written (defaultValue path); inputs.inputValues
  // already returned null/undefined so we don't double-check that here.
  return undefined;
}

export function useSignalFieldResolver(
  context: SignalFieldResolverContext,
): SignalFieldResolver {
  // ---- Auth path inputs ----
  const isAuth = context.kind === 'conversation';
  const conversation = useConversation(isAuth ? context.conversationId : null);
  const modelView = useModelView(isAuth ? context.conversationId : null);

  // ---- Public path inputs ----
  const isPublic = context.kind === 'public-deal';
  const publicDeal = usePublicDeal(isPublic ? context.shareId : null);
  const sharedModelView = useSharedModelView(isPublic ? context.shareId : null);

  // ---- Model identity (modelId) → useModel for displayConfig.signalFieldMappings + schema ----
  // Hook return shapes differ across the codebase (intentional inconsistency
  // — verified against existing call sites):
  //   - useConversation(id).data?.data?.modelId  (envelope NOT unwrapped + nested data wrapper)
  //   - useModel(id).data?.data?.displayConfig   (envelope NOT unwrapped)
  //   - usePublicDeal(shareId).data?.modelId      (envelope unwrapped in queryFn)
  //   - useModelView / useSharedModelView         (envelope unwrapped → direct ModelViewPayload)
  //
  // `deal-signal-overhaul` Phase 5 hotfix (2026-05-13): `useModel(modelId)`
  // hits `/api/models/:id` which is AUTH-ONLY. Public viewers' calls 401'd,
  // so `modelData` stayed undefined → `isReady=false` → all 5 Signal regions
  // (HeroReturns, ValueAddStory, BeforeAfterComparison, CapitalStackVisual,
  // AssumptionIntegrity) stuck in `data-state="loading"` skeleton forever
  // (~53 animate-pulse loaders permanently visible). Fix: skip `useModel`
  // for the public path entirely — derive the model schema from
  // `sharedModelView.data.schema` which the public model-view backend now
  // projects with `displayConfig` + `signal_variant` (Phase 5 hotfix on
  // `sharing.service.ts:1836`).
  const modelId = isAuth
    ? conversation.data?.data?.modelId
    : publicDeal.data?.modelId;
  // For the auth path, `useModel(modelId)` is the canonical schema source.
  // For the public path, we PREFER `sharedModelView.data.schema` (extended
  // 2026-05-13 to carry `displayConfig` + `signal_variant`); but we still
  // mint `useModel(modelId)` defensively so test mocks + any caller relying
  // on the old shape keep working. The auth-only 401 surfaces as
  // `modelQuery.data === undefined` for public callers — harmless since we
  // prefer `sharedModelView.data.schema` first.
  const modelQuery = useModel(modelId ?? null);

  return useMemo<SignalFieldResolver>(() => {
    const sourceData = isAuth ? modelView.data : sharedModelView.data;

    // Pick the model-schema source per path:
    //   - auth: `useModel(modelId).data.data` (full ModelSchema with
    //     `displayConfig` + `signal_variant` already populated)
    //   - public: PREFER `sharedModelView.data.schema` (the same subset,
    //     extended 2026-05-13 to include `displayConfig` + `signal_variant`),
    //     FALL BACK to `useModel(modelId).data.data` for back-compat with
    //     test mocks that still hand-roll the auth-side shape.
    const modelData = isAuth
      ? (modelQuery.data?.data as unknown as Record<string, unknown> | undefined)
      : ((sharedModelView.data?.schema ??
          (modelQuery.data?.data as unknown)) as Record<string, unknown> | undefined);

    const isReady = Boolean(modelData && sourceData);

    const mappings = (modelData?.displayConfig as { signalFieldMappings?: SignalFieldMappings } | undefined)?.signalFieldMappings;
    const schema = modelData as ResolutionInputs['schema']; // ModelSchema-shaped — has `outputs[]` for named-range lookup
    const inputValues = sourceData?.inputValues ?? {};
    const cellValues = sourceData?.cellValues ?? {};

    const resolutionInputs: ResolutionInputs = {
      mappings,
      schema,
      inputValues,
      cellValues,
    };

    return {
      resolve: (key) => {
        if (!isReady) return null;
        return resolveByMapping(resolutionInputs, key);
      },
      resolveTemplate: (key) => {
        if (!isReady) return null;
        // Template tokens can reference mapping keys OR direct named-ranges.
        // Try the mapping first; if absent, treat the raw key as the named-range.
        if (mappings && key in mappings) {
          return resolveByMapping(
            resolutionInputs,
            key as keyof SignalFieldMappings,
          );
        }
        return resolveByNamedRange(resolutionInputs, key);
      },
      isReady,
      schema: schema as ModelSchema | undefined,
    };
  }, [
    isAuth,
    modelView.data,
    sharedModelView.data,
    modelQuery.data?.data,
  ]);
}
